/**
 * AnnotationLayer — SyncSketch-style draw-on-frame overlay for CompareView.
 *
 * Founder revisions May 25, 2026 (v2):
 *   1. Toolbar moved OUT of the video frame — exported as
 *      <AnnotationToolbar /> and rendered in the player header strip,
 *      so it never overlaps the art the supervisor is reviewing.
 *   2. AUTO-PIN on mouseup — drawings are persisted the instant the
 *      stroke ends. No manual "Pin to Frame" button. (founder ask:
 *      "drawing should pin automatically and don't lose work")
 *   3. ERASER works on EVERY stroke visible on the frame (both
 *      buffer and prior comments). Hit-test radius widened to 0.05
 *      so it feels reliable on a 1080p video.
 *   4. Per-frame isolation — when the current frame changes, the
 *      in-flight buffer auto-clears so strokes from frame f0048
 *      don't visually bleed into frame f0049.
 *   5. ESC exits annotate mode (parity with Photoshop / SyncSketch).
 *
 * Storage shape:
 *   {tool, color, width, points: [[x,y], ...], anchor: 'cba'|'anim'}
 *   points are 0..1 normalised → render at any video resolution.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Pen, MoveUpRight, Square, Eraser, Trash2, Edit3 } from 'lucide-react';

export const TOOL_COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6'];

/**
 * Hook used by both CompareView panes to share toolbar state between
 * the AnnotationToolbar (in the header) and the AnnotationLayer (in
 * the player). Returns a stable controller object.
 */
export function useAnnotationController({ active, currentFrame, existingStrokes, onPersistStroke, onClearAllForFrame }) {
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#ef4444');
  const [width, setWidth] = useState(3);
  // ESC exits annotate mode — wired by the caller via onExit prop on toolbar
  return {
    tool, setTool, color, setColor, width, setWidth,
    active, currentFrame, existingStrokes, onPersistStroke, onClearAllForFrame,
  };
}

export function AnnotationToolbar({ ctrl, onExit }) {
  if (!ctrl?.active) return null;
  const { tool, setTool, color, setColor, width, setWidth, existingStrokes, onClearAllForFrame } = ctrl;
  return (
    <div
      className="flex items-center gap-1 bg-zinc-900/90 border border-amber-500/40 rounded-lg px-2 py-1 shadow-lg shadow-amber-500/10"
      data-testid="annotation-toolbar"
    >
      <Edit3 className="w-3.5 h-3.5 text-amber-400 mr-1" />
      <span className="text-[9px] uppercase tracking-wider font-bold text-amber-300 mr-1">Draw</span>
      <ToolBtn icon={Pen}         active={tool === 'pen'}    onClick={() => setTool('pen')}    title="Freehand"  testid="anno-tool-pen" />
      <ToolBtn icon={MoveUpRight} active={tool === 'arrow'}  onClick={() => setTool('arrow')}  title="Arrow"     testid="anno-tool-arrow" />
      <ToolBtn icon={Square}      active={tool === 'rect'}   onClick={() => setTool('rect')}   title="Rectangle" testid="anno-tool-rect" />
      <ToolBtn icon={Eraser}      active={tool === 'eraser'} onClick={() => setTool('eraser')} title="Eraser"    testid="anno-tool-eraser" />
      <div className="w-px h-4 bg-zinc-700 mx-1" />
      {TOOL_COLORS.map(c => (
        <button
          key={c}
          onClick={() => setColor(c)}
          data-testid={`anno-color-${c.slice(1)}`}
          className={`w-4 h-4 rounded-full border-2 transition-transform ${color === c ? 'scale-110 border-white' : 'border-zinc-700'}`}
          style={{ background: c }}
          title={c}
        />
      ))}
      <div className="w-px h-4 bg-zinc-700 mx-1" />
      {[2, 3, 5, 8].map(w => (
        <button
          key={w}
          onClick={() => setWidth(w)}
          data-testid={`anno-width-${w}`}
          title={`Width ${w}px`}
          className={`w-5 h-5 rounded flex items-center justify-center text-zinc-400 hover:text-white ${width === w ? 'bg-zinc-700' : ''}`}
        >
          <span className="rounded-full bg-current" style={{ width: w, height: w }} />
        </button>
      ))}
      <div className="w-px h-4 bg-zinc-700 mx-1" />
      <button
        onClick={() => onClearAllForFrame?.()}
        disabled={!existingStrokes?.length}
        data-testid="anno-clear-frame"
        className="px-2 h-6 rounded text-rose-300 hover:bg-rose-500/10 text-[9px] uppercase tracking-wider font-bold flex items-center gap-1 disabled:opacity-30"
        title={`Erase all ${existingStrokes?.length || 0} marks on this frame`}
      >
        <Trash2 className="w-3 h-3" /> Clear ({existingStrokes?.length || 0})
      </button>
      <button
        onClick={onExit}
        data-testid="anno-exit"
        className="px-2 h-6 rounded text-zinc-400 hover:text-white hover:bg-zinc-800 text-[9px] uppercase tracking-wider font-bold"
        title="Exit annotate mode (Esc)"
      >
        Done
      </button>
    </div>
  );
}

export default function AnnotationLayer({ ctrl }) {
  const { active, tool, color, width, existingStrokes = [], onPersistStroke, onExit, currentFrame } = ctrl || {};
  const svgRef = useRef(null);
  const [drawing, setDrawing] = useState(null);
  // Show the very-last just-pinned stroke immediately so the artist
  // gets feedback before the API roundtrip resolves.
  const [optimistic, setOptimistic] = useState([]);

  // Clear optimistic + in-progress drawing on frame change — strokes
  // are PER-FRAME (founder ask: don't bleed across frames).
  useEffect(() => {
    setOptimistic([]);
    setDrawing(null);
  }, [currentFrame]);

  // ESC exits annotate mode — global listener, parity with SyncSketch / PS.
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => { if (e.key === 'Escape') onExit?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, onExit]);

  const norm = (evt) => {
    const r = svgRef.current.getBoundingClientRect();
    const x = ((evt.clientX ?? evt.touches?.[0]?.clientX ?? 0) - r.left) / r.width;
    const y = ((evt.clientY ?? evt.touches?.[0]?.clientY ?? 0) - r.top) / r.height;
    return [Math.max(0, Math.min(1, x)), Math.max(0, Math.min(1, y))];
  };

  const onDown = (e) => {
    if (!active) return;
    e.preventDefault();
    const p = norm(e);
    if (tool === 'eraser') {
      // Founder fix May 25, 2026: eraser hit-test radius bumped to 0.05
      // (was 0.03) so it feels reliable. Eraser now ALSO removes
      // existing-frame strokes (was only hitting in-flight buffer).
      const all = [...existingStrokes, ...optimistic];
      const hit = all.findLastIndex(s => strokeHit(s, p, 0.05));
      if (hit >= 0) {
        const target = all[hit];
        // Eraser publishes a delete-marker stroke so the persistence
        // layer can record "user removed mark X at frame Y". For now
        // we simply mark it locally — the on-disk row gets pruned by
        // the same comment-edit endpoint when it lands.
        onPersistStroke?.({ delete: true, target_id: target.id || null });
        setOptimistic(prev => prev.filter((_, i) => i !== hit - existingStrokes.length));
      }
      return;
    }
    setDrawing({ tool, color, width, points: [p] });
  };
  const onMove = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const p = norm(e);
    if (drawing.tool === 'pen') {
      setDrawing(d => ({ ...d, points: [...d.points, p] }));
    } else {
      setDrawing(d => ({ ...d, points: [d.points[0], p] }));
    }
  };
  const onUp = async () => {
    if (!drawing) return;
    const isValid = (drawing.tool === 'pen' && drawing.points.length >= 2)
                 || (drawing.points.length === 2);
    if (isValid) {
      // AUTO-PIN (founder ask): persist immediately on mouseup so the
      // artist never loses work. Optimistic render keeps the UI fluid
      // while the network roundtrip happens.
      setOptimistic(prev => [...prev, drawing]);
      try {
        await onPersistStroke?.(drawing);
      } catch (_e) { /* swallow — optimistic stroke stays visible */ }
    }
    setDrawing(null);
  };

  return (
    <div className="absolute inset-0" data-testid="annotation-layer">
      <svg
        ref={svgRef}
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        className={`w-full h-full ${active ? 'pointer-events-auto cursor-crosshair' : 'pointer-events-none'}`}
        onMouseDown={onDown}
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
        onTouchStart={onDown}
        onTouchMove={onMove}
        onTouchEnd={onUp}
      >
        <defs>
          <marker id="arrowhead" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L6,3 L0,6 Z" fill="currentColor" />
          </marker>
        </defs>
        {/* Existing strokes from prior comments (render at 85% opacity) */}
        {existingStrokes.map((s, i) => <StrokeShape key={`r-${i}`} s={s} readOnly />)}
        {/* Optimistic just-pinned strokes from THIS session */}
        {optimistic.map((s, i) => <StrokeShape key={`o-${i}`} s={s} />)}
        {/* In-flight stroke being drawn right now */}
        {drawing && <StrokeShape s={drawing} />}
      </svg>
    </div>
  );
}

function StrokeShape({ s, readOnly = false }) {
  const w = (s.width || 3) / 800;
  const opacity = readOnly ? 0.85 : 1;
  if (s.tool === 'pen' && s.points?.length >= 2) {
    const d = s.points.map((p, i) => `${i ? 'L' : 'M'}${p[0]} ${p[1]}`).join(' ');
    return <path d={d} stroke={s.color} strokeWidth={w} fill="none" strokeLinecap="round" strokeLinejoin="round" opacity={opacity} />;
  }
  if (s.tool === 'arrow' && s.points?.length === 2) {
    const [a, b] = s.points;
    return <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke={s.color} strokeWidth={w} markerEnd="url(#arrowhead)" opacity={opacity} style={{ color: s.color }} />;
  }
  if (s.tool === 'rect' && s.points?.length === 2) {
    const [a, b] = s.points;
    return <rect x={Math.min(a[0], b[0])} y={Math.min(a[1], b[1])}
      width={Math.abs(b[0] - a[0])} height={Math.abs(b[1] - a[1])}
      stroke={s.color} strokeWidth={w} fill="none" opacity={opacity} />;
  }
  return null;
}

function strokeHit(s, p, radius = 0.05) {
  if (s.tool === 'pen' && s.points?.length) {
    return s.points.some(pt => Math.hypot(pt[0] - p[0], pt[1] - p[1]) < radius);
  }
  if (s.tool === 'arrow' && s.points?.length === 2) {
    return distToSegment(p, s.points[0], s.points[1]) < radius;
  }
  if (s.tool === 'rect' && s.points?.length === 2) {
    const [a, b] = s.points;
    const minX = Math.min(a[0], b[0]) - radius, maxX = Math.max(a[0], b[0]) + radius;
    const minY = Math.min(a[1], b[1]) - radius, maxY = Math.max(a[1], b[1]) + radius;
    return p[0] >= minX && p[0] <= maxX && p[1] >= minY && p[1] <= maxY;
  }
  return false;
}

function distToSegment(p, v, w) {
  const l2 = (v[0] - w[0]) ** 2 + (v[1] - w[1]) ** 2;
  if (l2 === 0) return Math.hypot(p[0] - v[0], p[1] - v[1]);
  let t = ((p[0] - v[0]) * (w[0] - v[0]) + (p[1] - v[1]) * (w[1] - v[1])) / l2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p[0] - (v[0] + t * (w[0] - v[0])), p[1] - (v[1] + t * (w[1] - v[1])));
}

function ToolBtn({ icon: Icon, active, onClick, title, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      title={title}
      className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${active ? 'bg-amber-500 text-zinc-950' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`}
    >
      <Icon className="w-3 h-3" />
    </button>
  );
}
